import { useEffect, useRef, useState } from "react";
import { useSocket } from "../../contexts/SocketContext";

export default function Live() {
  const localVideoRef = useRef();
  const remoteVideoRef = useRef();
  const [isAdmin, setIsAdmin] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showStartBtn, setShowStartBtn] = useState(false);
  const [preRecordedVideo, setPreRecordedVideo] = useState(true);
  const [peerConnection, setPeerConnection] = useState(null);
  const [showWatchNowBtn, setShowWatchNowBtn] = useState(false);
  const [isWatching, setIsWatching] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoStopped, setIsVideoStopped] = useState(false);
  const [currentFacingMode, setCurrentFacingMode] = useState("user"); // "user" or "environment"
  const [currentStream, setCurrentStream] = useState(null);
  const socket = useSocket();

  // Socket IO event handlers
  useEffect(() => {
    if (!socket) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("user") === "admin") setIsAdmin(true);

    socket.emit("join");

    socket.on("live_status", (data) => {
      setIsStreaming(data.isStreaming);
      if (data.isStreaming) {
        setPreRecordedVideo(false);
        // Show "Watch Now" button for viewers when stream starts
        if (!isAdmin) {
          setShowWatchNowBtn(true);
        }
      } else {
        setPreRecordedVideo(true);
        setShowWatchNowBtn(false);
        setIsWatching(false);
      }
    });

    // Listen for streaming started event (when admin starts)
    socket.on("streaming_started", () => {
      setIsStreaming(true);
      setPreRecordedVideo(false);
      if (!isAdmin) {
        setShowWatchNowBtn(true);
      }
    });

    // Listen for live stream started event
    socket.on("live_stream_started", (data) => {
      console.log("Stream started:", data);
      setIsStreaming(true);
      setPreRecordedVideo(false);
      if (!isAdmin) {
        setShowWatchNowBtn(true);
      }
    });

    // Listen for viewer count updates
    socket.on("viewer_count_update", (data) => {
      setViewerCount(data.count);
    });

    // Admin: Handle viewer requesting stream
    socket.on("viewer_requesting_stream", async ({ viewerId }) => {
      if (!peerConnection || !isAdmin) return;

      console.log("Viewer requesting stream:", viewerId);

      try {
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);

        socket.emit("offer", {
          sdp: offer,
          to: viewerId,
          from: socket.id,
        });
      } catch (error) {
        console.error("Error creating offer:", error);
      }
    });

    socket.on("offer", async ({ sdp, from }) => {
      // Only viewers receive offers from admin
      if (isAdmin) return;

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      setPeerConnection(pc);

      // Viewer receives tracks from admin (receive-only)
      pc.ontrack = (event) => {
        console.log("📺 Receiving stream from admin:", event.streams[0]);
        setTimeout(() => {
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = event.streams[0];
          }
        }, 0);
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit("ice-candidate", {
            candidate: event.candidate,
            to: from,
          });
        }
      };

      // Create answer without adding any tracks (receive-only)
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      socket.emit("answer", { sdp: answer, to: from, from: socket.id });
    });

    socket.on("answer", async ({ sdp }) => {
      if (!peerConnection) return;
      await peerConnection.setRemoteDescription(new RTCSessionDescription(sdp));
    });

    socket.on("ice-candidate", async (candidate) => {
      if (!peerConnection) return;
      await peerConnection.addIceCandidate(candidate);
    });
    return () => {
      socket.off("live_status");
      socket.off("streaming_started");
      socket.off("live_stream_started");
      socket.off("viewer_count_update");
      socket.off("viewer_requesting_stream");
      socket.off("offer");
      socket.off("answer");
      socket.off("ice-candidate");
    };
  }, [socket, peerConnection, isAdmin]);

  const handlePasswordCheck = async () => {
    if (password === "qwer1234") {
      setPasswordVerified(true);
    } else {
      alert("Wrong password");
    }
  };

  const handleStartStreaming = async () => {
    try {
      // Request video and audio from admin's device
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      console.log("🎥 Admin starting stream with:", stream);

      // Set streaming to true first to render the video element
      setIsStreaming(true);
      socket.emit("live_stream_started");

      // Wait for the video element to be rendered
      setTimeout(() => {
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          localVideoRef.current.muted = true;
        }
      }, 0);

      // Create peer connection with admin's stream
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      // Add admin's video and audio tracks to the connection
      stream.getTracks().forEach((track) => {
        console.log(`➕ Adding track: ${track.kind}`);
        pc.addTrack(track, stream);
      });

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit("ice-candidate", {
            candidate: event.candidate,
            to: null,
          });
        }
      };

      setPeerConnection(pc);
      setCurrentStream(stream);
      setShowStartBtn(false);
    } catch (error) {
      console.error("❌ Error starting stream:", error);
      alert("Failed to access camera/microphone. Please grant permissions.");
    }
  };

  // Toggle audio mute/unmute
  const toggleAudioMute = () => {
    if (currentStream) {
      const audioTrack = currentStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioMuted(!audioTrack.enabled);
        console.log(`🔊 Audio ${audioTrack.enabled ? "unmuted" : "muted"}`);
      }
    }
  };

  // Toggle video on/off
  const toggleVideo = () => {
    if (currentStream) {
      const videoTrack = currentStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoStopped(!videoTrack.enabled);
        console.log(`📹 Video ${videoTrack.enabled ? "enabled" : "disabled"}`);
      }
    }
  };

  // Rotate camera (switch between front and back)
  const rotateCamera = async () => {
    try {
      // Stop current stream
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop());
      }

      // Toggle facing mode
      const newFacingMode =
        currentFacingMode === "user" ? "environment" : "user";

      // Get new stream with opposite camera
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      console.log(`🔄 Camera rotated to: ${newFacingMode}`);

      // Update video element
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = newStream;
      }

      // Replace tracks in peer connection
      if (peerConnection) {
        const senders = peerConnection.getSenders();
        const videoSender = senders.find(
          (sender) => sender.track?.kind === "video"
        );
        const audioSender = senders.find(
          (sender) => sender.track?.kind === "audio"
        );

        if (videoSender) {
          videoSender.replaceTrack(newStream.getVideoTracks()[0]);
        }
        if (audioSender) {
          audioSender.replaceTrack(newStream.getAudioTracks()[0]);
        }
      }

      setCurrentStream(newStream);
      setCurrentFacingMode(newFacingMode);

      // Restore mute states
      if (isAudioMuted) {
        newStream.getAudioTracks()[0].enabled = false;
      }
      if (isVideoStopped) {
        newStream.getVideoTracks()[0].enabled = false;
      }
    } catch (error) {
      console.error("❌ Error rotating camera:", error);
      alert(
        "Failed to rotate camera. Your device may not have multiple cameras."
      );
    }
  };

  const handleStopStreaming = () => {
    if (peerConnection) {
      peerConnection.getSenders().forEach((s) => s.track.stop());
      peerConnection.close();
      setPeerConnection(null);
    }

    if (currentStream) {
      currentStream.getTracks().forEach((track) => track.stop());
      setCurrentStream(null);
    }

    if (localVideoRef.current && localVideoRef.current.srcObject) {
      localVideoRef.current.srcObject
        .getTracks()
        .forEach((track) => track.stop());
    }

    socket.emit("stop_stream");
    setIsStreaming(false);
    setShowStartBtn(true);
    setIsAudioMuted(false);
    setIsVideoStopped(false);
  };

  const handleWatchNow = async () => {
    // Get browser and user info
    const userAgent = navigator.userAgent;
    const browserInfo = {
      platform: navigator.platform,
      language: navigator.language,
      userAgent: userAgent,
      browser: getBrowserName(userAgent),
      screenResolution: `${window.screen.width}x${window.screen.height}`,
    };

    // Send viewer details to server
    socket.emit("viewer_joined", {
      viewerId: socket.id,
      timestamp: new Date().toISOString(),
      browserInfo: browserInfo,
    });

    setShowWatchNowBtn(false);
    setIsWatching(true);

    // Create receive-only peer connection for viewer
    try {
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      // Viewer only receives tracks (no sending)
      pc.ontrack = (event) => {
        console.log("📺 Received track from admin:", event.streams[0]);
        setTimeout(() => {
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = event.streams[0];
          }
        }, 0);
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit("ice-candidate", {
            candidate: event.candidate,
            to: "admin",
          });
        }
      };

      setPeerConnection(pc);

      // Send request to admin to start connection
      // Admin will create offer and send it to this viewer
      socket.emit("request_stream", { from: socket.id });
    } catch (error) {
      console.error("Error setting up peer connection:", error);
    }
  };

  // Helper function to get browser name
  const getBrowserName = (userAgent) => {
    if (userAgent.includes("Firefox")) return "Firefox";
    if (userAgent.includes("Chrome") && !userAgent.includes("Edg"))
      return "Chrome";
    if (userAgent.includes("Safari") && !userAgent.includes("Chrome"))
      return "Safari";
    if (userAgent.includes("Edg")) return "Edge";
    if (userAgent.includes("Opera") || userAgent.includes("OPR"))
      return "Opera";
    return "Unknown";
  };

  return (
    <div style={{ textAlign: "center", padding: "20px" }}>
      <h1>Live TV Screen</h1>

      {/* Admin viewer count display */}
      {isAdmin && isStreaming && (
        <div
          style={{ marginBottom: "10px", fontSize: "18px", fontWeight: "bold" }}
        >
          👥 Live Viewers: {viewerCount}
        </div>
      )}

      {/* Pre-recorded video when stream is offline */}
      {/* {!isAdmin && preRecordedVideo && !isWatching && !showWatchNowBtn && (
        <div>
          <h3>Stream is currently offline</h3>
          <video
            src="/offline-video.mp4"
            controls
            autoPlay
            muted
            loop
            width="600"
            style={{ border: "2px solid black" }}
          />
          <p style={{ marginTop: "10px", color: "#666" }}>
            Waiting for stream to start...
          </p>
        </div>
      )} */}

      {/* Watch Now button for viewers when stream starts */}
      {!isAdmin && showWatchNowBtn && !isWatching && (
        <div style={{ marginTop: "20px" }}>
          <h2 style={{ color: "#ff0000" }}>🔴 Live Stream is Now Available!</h2>
          <button
            onClick={handleWatchNow}
            style={{
              padding: "15px 30px",
              fontSize: "18px",
              backgroundColor: "#ff0000",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              marginTop: "10px",
              fontWeight: "bold",
            }}
          >
            🎬 Watch Now
          </button>
        </div>
      )}

      {/* Admin password verification */}
      {isAdmin && !passwordVerified && (
        <div>
          <input
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ padding: "10px", marginRight: "10px" }}
          />
          <button onClick={handlePasswordCheck}>Verify</button>
        </div>
      )}

      {/* Admin start streaming button */}
      {passwordVerified && !isStreaming && (
        <div>
          <button
            onClick={handleStartStreaming}
            style={{
              padding: "15px 30px",
              fontSize: "18px",
              backgroundColor: "#00aa00",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Start Streaming
          </button>
        </div>
      )}

      {/* Streaming video display */}
      {isStreaming && (
        <div>
          {isAdmin && (
            <div style={{ marginBottom: "10px" }}>
              <button
                onClick={handleStopStreaming}
                style={{
                  padding: "10px 20px",
                  fontSize: "16px",
                  backgroundColor: "#cc0000",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Stop Streaming
              </button>
            </div>
          )}
          <div className="flex flex-col justify-center items-center">
            {isAdmin ? (
              <>
                <h3 className="mb-2">Your Stream (Admin View)</h3>
                <video
                  ref={localVideoRef}
                  autoPlay
                  muted
                  width="600"
                  style={{ border: "2px solid red" }}
                />

                {/* Admin Controls */}
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginTop: "15px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <button
                    onClick={toggleAudioMute}
                    style={{
                      padding: "10px 20px",
                      fontSize: "14px",
                      backgroundColor: isAudioMuted ? "#ff6b6b" : "#4CAF50",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    {isAudioMuted ? "🔇 Unmute Audio" : "🔊 Mute Audio"}
                  </button>

                  <button
                    onClick={toggleVideo}
                    style={{
                      padding: "10px 20px",
                      fontSize: "14px",
                      backgroundColor: isVideoStopped ? "#ff6b6b" : "#2196F3",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    {isVideoStopped ? "📹 Enable Video" : "📹 Disable Video"}
                  </button>

                  <button
                    onClick={rotateCamera}
                    style={{
                      padding: "10px 20px",
                      fontSize: "14px",
                      backgroundColor: "#FF9800",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    🔄 Rotate Camera
                  </button>
                </div>

                <div
                  style={{ marginTop: "10px", fontSize: "12px", color: "#666" }}
                >
                  <p>
                    {isAudioMuted && "⚠️ Audio is muted | "}
                    {isVideoStopped && "⚠️ Video is disabled | "}
                    📱 Camera: {currentFacingMode === "user" ? "Front" : "Back"}
                  </p>
                </div>
              </>
            ) : isWatching ? (
              <>
                <h3>🔴 Live Stream</h3>
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  width="600"
                  style={{
                    border: "2px solid green",
                    backgroundColor: "#000",
                    transition: "opacity 0.3s ease",
                  }}
                  controls={false}
                />

                {/* Viewer Controls */}
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginTop: "15px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <button
                    onClick={() => {
                      if (remoteVideoRef.current) {
                        remoteVideoRef.current.muted =
                          !remoteVideoRef.current.muted;
                        setIsAudioMuted(remoteVideoRef.current.muted);
                      }
                    }}
                    style={{
                      padding: "10px 20px",
                      fontSize: "14px",
                      backgroundColor: isAudioMuted ? "#ff6b6b" : "#4CAF50",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    {isAudioMuted ? "🔇 Unmute Audio" : "🔊 Mute Audio"}
                  </button>

                  <button
                    onClick={() => {
                      if (remoteVideoRef.current) {
                        if (isVideoStopped) {
                          remoteVideoRef.current.style.opacity = "1";
                          setIsVideoStopped(false);
                        } else {
                          remoteVideoRef.current.style.opacity = "0";
                          setIsVideoStopped(true);
                        }
                      }
                    }}
                    style={{
                      padding: "10px 20px",
                      fontSize: "14px",
                      backgroundColor: isVideoStopped ? "#ff6b6b" : "#2196F3",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    {isVideoStopped ? "📹 Show Video" : "📹 Hide Video"}
                  </button>
                </div>

                <div
                  style={{ marginTop: "10px", fontSize: "12px", color: "#666" }}
                >
                  <p>
                    🔇 Viewer mode - You are watching the stream
                    {isAudioMuted && " | ⚠️ Audio is muted"}
                    {isVideoStopped && " | ⚠️ Video is hidden"}
                  </p>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
